import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useWtrSelector } from 'redux/hooks';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { colourScallopGrey } from '@johnlewispartnership/wtr-ingredients/foundations/colours';

import { FORMAT, format } from 'utils/date';
import urls from 'constants/urls';
import { BREAKPOINTS } from 'constants/grid';
import wcsConfig from 'utils/wcs-config';

import Skeleton from 'components/Skeleton';
import ContentLocation from 'components/ContentLocation';
import Breadcrumbs from 'components/Breadcrumbs';
import Canonical from 'components/Canonical';
import ProductGrid from 'components/ProductGrid';
import { Snackbar } from '@johnlewispartnership/wtr-ingredients/ingredients/Snackbar';

import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { useClientSideMediaQuery } from 'hooks/use-client-side-media-query';

import Builder, { CountAndTotal } from 'components/Builder';
import { getGridItems, getSearchAndBrowseLoaded } from 'redux/modules/search-and-browse/selectors';
import { getLoading } from 'redux/modules/cms-page/selectors';
import { getPageLoading } from 'redux/modules/page/selectors';
import { makeWineProgressMsg } from 'components/Offers/utils/make-wine-progress-message';
import { BaseBuilder, BaseGroup } from 'components/Builder/types';
import {
  GenericOfferLineItemPlaceholder,
  WineOfferLineItemPlaceholder,
} from './OfferDetailsPlaceholders';
import WineOfferFilterChips from './WineOfferFilterChips';
import styles from './OfferDetails.scss';
import { SEARCH_TYPE } from './constants';

const breadcrumbs = [
  {
    name: 'Groceries',
    path: urls.groceriesHome,
  },
  {
    name: 'Offers',
    path: urls.offers,
  },
];

const taxonomyLevel = 'F0';
const location = 'offer';

const breadcrumbsHeight = 52;
const siteHeaderHeight = 108;

const stickyScrollDownThreshold = 60;
const stickyScrollUpThreshold = 120;
export interface PromotionBuilders extends BaseBuilder<BaseGroup> {
  promotionDescription: string;
}

export interface OfferDetailsProps {
  history: { goBack: () => void };
  promotion: { promotionDescription?: string; promotionExpiryDate?: string };
  promotionId?: string;
  promotionBuilders?: PromotionBuilders[];
  isWineBuilder: boolean;
}

const OfferDetails = memo(
  ({
    promotion,
    promotionId,
    promotionBuilders = [],
    isWineBuilder = false,
  }: OfferDetailsProps) => {
    const { promotionDescription = 'Offer(s)', promotionExpiryDate } = promotion || {};

    const isSearchAndBrowseLoaded = useWtrSelector(getSearchAndBrowseLoaded);
    const isContentLoading = useWtrSelector(getLoading);
    const isPageLoading = useWtrSelector(getPageLoading);
    const loading = isPageLoading || !isSearchAndBrowseLoaded || isContentLoading;

    const products = useWtrSelector(getGridItems);
    const getProducts = useCallback(() => products, [products]);

    const [activeChipId, setActiveChipId] = useState<number>(0);
    const [isSticky, setSticky] = useState<boolean>(false);
    const [isHidden, setHidden] = useState<boolean>(false);
    const [productCountAndTotal, setProductCountAndTotal] = useState<CountAndTotal | null>(null);

    const snackMsg = makeWineProgressMsg(productCountAndTotal, promotionBuilders, isWineBuilder);

    const lastScrollPos = useRef<number>(0);
    const headerHeight = useRef<number>(0);

    const isBuilderPage = promotionBuilders.length;

    const initialMessage = isWineBuilder ? 'Build your wine offer' : 'Build your offer';
    const completeMessage = isWineBuilder ? "You've qualified for the offer" : 'Offer complete';

    const isMobileOrTablet = useClientSideMediaQuery({ maxWidth: BREAKPOINTS.lg });

    const GroupItemPlaceholder = isWineBuilder
      ? WineOfferLineItemPlaceholder
      : GenericOfferLineItemPlaceholder;

    const chipsRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
      const chipsRect = (chipsRef.current as HTMLDivElement).getBoundingClientRect();
      const containerRect = (wrapperRef.current as HTMLDivElement).getBoundingClientRect();
      const offset = breadcrumbsHeight + siteHeaderHeight + headerHeight.current;
      const scrollingUp = window.scrollY < lastScrollPos.current;

      setSticky(previousSticky => {
        const sticky = previousSticky
          ? containerRect.top + offset < stickyScrollDownThreshold
          : chipsRect.top < -stickyScrollUpThreshold;

        setHidden(sticky && !scrollingUp);

        return sticky;
      });

      lastScrollPos.current = window.scrollY;
    };

    useEffect(() => {
      if (loading || !isBuilderPage || !isWineBuilder || isMobileOrTablet) {
        return () => {};
      }

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, [loading, isBuilderPage, isWineBuilder, isMobileOrTablet]);

    useEffect(() => {
      if (!loading) {
        headerHeight.current = (headerRef.current as HTMLDivElement).getBoundingClientRect().height;
      }
    }, [loading]);

    return (
      <>
        <Helmet title={promotionDescription} />
        <Canonical href={wcsConfig.canonicalOfferDetailsUrl({ offerId: promotionId })} />
        <div className={styles.wrapper} ref={wrapperRef}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          {loading ? (
            <Skeleton
              withSpinner
              spinnerPosition="top"
              height="100vh"
              backgroundColor={colourScallopGrey}
              data-testid="products-loading"
            />
          ) : (
            <>
              <div className="container-fluid" ref={headerRef}>
                <header className={styles.header} id="offerMeta">
                  <h1 className={styles.title}>{promotionDescription}</h1>
                  <ContentLocation name="header" preloadImages />
                  {promotionExpiryDate && (
                    <p className={styles.expiryNotification}>
                      Offer available for delivery or collection until{' '}
                      {format(promotionExpiryDate, FORMAT.DAY_MONTH_YEAR_SLASH)}.
                    </p>
                  )}
                </header>
              </div>
              {isBuilderPage ? (
                <>
                  {isWineBuilder && (
                    <WineOfferFilterChips
                      ref={chipsRef}
                      sticky={isSticky}
                      hidden={isHidden}
                      activeChipId={activeChipId}
                      setActiveChipId={setActiveChipId}
                    />
                  )}
                  <div className={classNames(styles.offersContainer, 'container-fluid')}>
                    <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
                      <div
                        className={classNames(
                          'col-sm-12of12',
                          'col-lg-8of12',
                          styles.productsContainer,
                        )}
                      >
                        <ProductGrid isBuilderPage />
                      </div>
                    </SSRMediaQuery>
                    <Builder
                      alwaysOpen={isWineBuilder}
                      className={classNames(styles.offersBuilder, {
                        [styles.stickyFilterOffset]: isSticky && !isHidden,
                      })}
                      builders={promotionBuilders}
                      GroupItemPlaceholder={GroupItemPlaceholder}
                      location={location}
                      getProducts={getProducts}
                      searchType={SEARCH_TYPE}
                      taxonomyLevel={taxonomyLevel}
                      completeMessage={completeMessage}
                      initialMessage={initialMessage}
                      multipleMessage="Build another offer"
                      {...(isWineBuilder && {
                        mobileHeader: (
                          <WineOfferFilterChips
                            mobile
                            activeChipId={activeChipId}
                            setActiveChipId={setActiveChipId}
                          />
                        ),
                        setProductCountAndTotal,
                      })}
                    />
                  </div>
                </>
              ) : (
                <ProductGrid />
              )}
            </>
          )}
        </div>
        {snackMsg && (
          <Snackbar
            dataTest="progressInfo"
            open
            placement="bottom"
            snapTo="window"
            type="success"
            onClose={() => setProductCountAndTotal(null)}
            // eslint-disable-next-line
            // @ts-ignore
            duration={3000}
          >
            {snackMsg}
          </Snackbar>
        )}
      </>
    );
  },
);

OfferDetails.displayName = 'OfferDetails';

export default OfferDetails;
